import React, { useState } from 'react';

import Head from 'next/head';
import { InferGetServerSidePropsType } from 'next';
import styled from 'styled-components';
import parse from 'html-react-parser';

import { colors, media_sm, media_lg } from 'theme';
import { useWindowSize } from 'hooks';
import { ReviewsApi } from 'services';
import { convertReviewsToApp } from 'mappers';
import { Carousel, Rating, BackgroundStaticStars } from 'atoms';
import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';

export async function getServerSideProps() {
  const { results, count } = await ReviewsApi.getAll({});

  const reviews = convertReviewsToApp(results);

  return {
    props: {
      reviews,
      count,
    },
  };
}

export default function Reviews({ reviews, count }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [currentBackground, setCurrentBackground] = useState(0);
  const { width = 0 } = useWindowSize();

  const handleCarouselBeforeChange = (curr: number, next: number) => {
    setCurrentSlide(next + 1);
    setCurrentBackground((v) => v + 1);

    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const schemaOrg = reviews.map((item) => ({
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: item.title,
    datePublished: item.date,
    reviewBody: item.description,
    reviewRating: {
      '@type': 'Rating',
      bestRating: '5',
      ratingValue: item.rating,
      worstRating: '1',
    },
  }));

  return (
    <>
      <Head>
        <title>Отзывы</title>
        {/* eslint-disable-next-line react/no-danger */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }} />
      </Head>
      <Title>Отзывы</Title>
      <CarouselWrapper>
        <CarouselStyled
          adaptiveHeight={width <= 768}
          arrowColor={colors.lightGrey}
          dotsElement={
            count > 9 || width <= 768 ? (
              <PaginationNumbers>
                {currentSlide} / {count}
              </PaginationNumbers>
            ) : null
          }
          beforeChange={handleCarouselBeforeChange}
        >
          {reviews.map(({ id, date, description, photo, rating, title, win }) => (
            <CarouselItem key={id}>
              <Review>
                <Avatar src={photo} />
                <ReviewContent>
                  <Surname>{title}</Surname>
                  <ReviewDate>{format(new Date(date), 'dd MMMM yyyy', { locale: ru })}</ReviewDate>
                  <Prize>{win}</Prize>
                  <Rate>
                    <Rating rating={rating} />
                  </Rate>
                  <Description>{parse(description)}</Description>
                </ReviewContent>
              </Review>
            </CarouselItem>
          ))}
        </CarouselStyled>
        <BackgroundStaticStars currentBackground={currentBackground} />
      </CarouselWrapper>
    </>
  );
}

const Title = styled.h1`
  margin: 0px;
  margin-left: 10px;

  color: ${colors.mainText};
  font-size: 24px;
  font-weight: 700;

  ${media_sm} {
    margin-left: 0;

    font-size: 50px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  padding-bottom: 30px;

  ${media_sm} {
    padding-bottom: 50px;
  }
`;

const CarouselStyled = styled(Carousel)`
  margin: 0px;

  .slick-list {
    width: 100vw;
    padding: 0 calc(50vw - 150px);
    margin-left: calc(-50vw + 150px);

    position: relative;

    ${media_sm} {
      width: auto;
      padding: 0;
      margin-left: 0;
    }
  }

  .slick-track {
    ${media_sm} {
      display: flex;
      align-items: center;
    }
  }

  .slick-prev,
  .slick-next,
  .slick-prev:hover,
  .slick-next:hover {
    bottom: 2px;
    top: unset;

    ${media_sm} {
      width: 23px;
      height: 44px;

      top: calc(50% - 20px);
      bottom: unset;
    }
  }
  .slick-prev,
  .slick-prev:hover {
    ${media_lg} {
      left: 80px;
    }
  }
  .slick-next,
  .slick-next:hover {
    ${media_lg} {
      right: 80px;
    }
  }

  .slick-dots.slick-dots-bottom {
    ${media_sm} {
      top: unset;
      bottom: -20px;
    }
  }
`;

const CarouselItem = styled.div`
  margin-left: calc(-50vw + 160px);
  padding: 50px calc(50vw - 160px);

  ${media_sm} {
    margin-left: 0;
    padding: 50px 100px;
  }

  ${media_lg} {
    padding: 50px 200px;
    height: 100%;
  }
`;

const PaginationNumbers = styled.div`
  && {
    bottom: -10px;
  }

  color: #a5a5a5;
  font-weight: 600;
  font-size: 30px;
`;

const Review = styled.div`
  padding: 35px 20px 25px;

  display: flex;
  flex-direction: column;

  position: relative;

  background-color: ${colors.mainBg};
  transition: background-color 0.5s;
  border-radius: 20px;
  box-shadow: 0px 0px 25px 9px rgba(0, 0, 0, 0.1);

  ${media_sm} {
    padding: 40px 50px 34px 40px;

    flex-direction: row;
  }
`;

const ReviewContent = styled.div`
  ${media_sm} {
    width: calc(100% - 70px);
    padding-left: 30px;
  }
`;

const Avatar = styled.img`
  width: 60px;
  height: 60px;

  position: absolute;
  top: 35px;
  object-fit: cover;

  border-radius: 30px;

  ${media_sm} {
    width: 70px;
    height: 70px;

    position: static;

    border-radius: 35px;
  }
`;

const Surname = styled.div`
  width: calc(100% - 70px);
  min-height: 70px;
  margin-left: 70px;
  margin-top: 10px;
  margin-bottom: 10px;

  color: ${colors.mainText};
  font-size: 20px;
  font-weight: 700;
  line-height: 25px;

  ${media_sm} {
    width: calc(100% - 130px);
    min-height: unset;
    margin-left: 0px;
    margin-top: 0;
  }
`;

const ReviewDate = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;

  color: ${colors.lightGrey};
  font-size: 12px;

  ${media_sm} {
    top: 42px;
    right: 43px;

    font-size: 16px;
  }
`;

const Prize = styled.div`
  color: ${colors.pink};
  font-size: 20px;
  line-height: 25px;
`;

const Rate = styled.div`
  height: 35px;
  padding-top: 11px;

  color: ${colors.lightGrey};
`;

const Description = styled.div`
  padding-top: 18px;

  color: ${colors.mainText};

  ${media_sm} {
    font-size: 18px;
    font-weight: 400;
  }
`;
